import ApiService from './api.service';

export type UseCaseStatus = 'PENDING' | 'CONNECTED' | 'VALIDATING' | 'CHANGES_REQUESTED' | 'APPROVED' | 'PLAN_APPROVED' | 'IMPLEMENTATION_IN_PROGRESS' | 'IMPLEMENTATION_COMPLETE';

export type UseCaseDataSource = {
  id: string;
  name: string;
  description?: string;
  source_id?: string | null;
  status: UseCaseStatus;
  thread_id?: string | null;
  validation_kb_article_id?: string | null;
};

export type UseCase = {
  id: string;
  name: string;
  description?: string;
  data_sources: UseCaseDataSource[];
  discovery_status: UseCaseStatus;
  discovery_thread_id?: string | null;
  discovery_kb_article_id?: string | null;
  data_modeling_status: UseCaseStatus;
  data_modeling_thread_id?: string | null;
  data_modeling_kb_article_id?: string | null;
};

export type SingleRecordResponse<T> = {
  record: T;
};

export type ListRecordsResponse<T> = {
  records: T[];
  total_records?: number;
  total_pages?: number;
};

export class UseCaseService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
  }): Promise<ListRecordsResponse<UseCase>> {
    const {
      page = 1,
      pageSize = 50,
      filters = {},
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
      ...filters,
    };

    const result = await ApiService.getInstance().request(
      'GET',
      '/use-cases',
      queryParams
    ) as ListRecordsResponse<UseCase>;

    return result;
  }

  async findById(id: string): Promise<UseCase> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/use-cases/${id}`
    ) as SingleRecordResponse<UseCase>;
    return result.record;
  }

  async create(useCase: Partial<UseCase>): Promise<UseCase> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/use-cases',
      useCase
    ) as SingleRecordResponse<UseCase>;
    return result.record;
  }

  async save(useCase: Partial<UseCase>): Promise<UseCase> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/use-cases/${useCase.id}`,
      useCase
    ) as SingleRecordResponse<UseCase>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/use-cases/${id}`
    );
  }
}

export const useCaseService = new UseCaseService();
